import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import {
  ButtonMode,
  ButtonTheme,
  ButtonType,
  InputType,
  LoaderTheme,
  ModalButtons,
  NotificationButtonStyle,
  NotificationLayout,
  NotificationTheme,
  TabsComponent,
  TabTheme,
} from '@fcom/ui-components';
import { ElementActions, ElementTypes } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm';
import { stopPropagation, unsubscribe } from '@fcom/core/utils';
import { finShare } from '@fcom/rx';
import { GaContext } from '@fcom/common/interfaces/gtm.interface';
import { LoyaltyRoutesPath } from '@fcom/loyalty-core/interfaces';
import { ConfigService, mapError } from '@fcom/core';
import { Profile } from '@fcom/core-api/login';

import {
  isValidate2faEmailVerificationCodeErrorResponse,
  TwoFactorAuthService,
  TwoFactorAuthStatus,
  TwoFactorAuthType,
} from '../../services/two-factor-auth.service';
import { ProfileService } from '../../services/profile.service';
import { AviosLinkingService, AviosAccountLinkingStatus } from '../../../loyalty-plus/services/avios-linking.service';

export enum AuthState {
  CHOOSE_TYPE = 'CHOOSE_TYPE',
  VERIFY = 'VERIFY',
}

export enum AuthError {
  Enable2faSms = 'enable2faSms',
  Enable2faApp = 'enable2faApp',
  Enable2faEmail = 'enable2faEmail',
  Resend2faEmail = 'resend2faEmail',
  ValidateCode = 'validateCode',
  IncorrectCode = 'incorrectCode',
  Disable2fa = 'disable2fa',
}

export enum TrackGtmActivatingStatus {
  ACTIVATING_SUCCESSFUL = 'activating-successful',
  ACTIVATING_FAILED = 'activating-failed',
}

@Component({
  selector: 'fin-loyalty-two-factor-auth-toggle',
  templateUrl: 'loyalty-two-factor-auth-toggle.component.html',
  styleUrls: ['./loyalty-two-factor-auth-toggle.component.scss'],
})
export class LoyaltyTwoFactorAuthToggleComponent implements OnInit, OnDestroy {
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly TabTheme = TabTheme;
  readonly TwoFactorAuthType = TwoFactorAuthType;
  readonly TwoFactorAuthStatus = TwoFactorAuthStatus;
  readonly AuthState = AuthState;
  readonly ModalButtons = ModalButtons;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationButtonStyle = NotificationButtonStyle;
  readonly LoaderTheme = LoaderTheme;
  readonly LoyaltyRoutesPath = LoyaltyRoutesPath;
  readonly AviosAccountLinkingStatus = AviosAccountLinkingStatus;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly InputType = InputType;
  readonly ButtonMode = ButtonMode;
  readonly AuthError = AuthError;

  @HostBinding('attr.tabindex') tabindex = -1;
  @HostBinding('class.no-outline') noOutline = true;

  @Input() enabledBackButtonUrl: string;

  qrCodeImageUrl$: Observable<string>;
  parsedMobilePhone$: Observable<string>;

  isTwoFactorAuthEnabled$: Observable<boolean>;
  aviosLinkingStatus$: Observable<AviosAccountLinkingStatus>;
  profile$: Observable<Profile>;

  authState: AuthState = AuthState.CHOOSE_TYPE;
  secretKey: string;
  isEnable2faSmsConfirmationModalOpen = false;
  isDisableConfirmationModalOpened = false;
  isRequestLoading = false;
  authError: AuthError = undefined;
  resendEmailCount = 0;
  showSend2faEmailVerificationCodeSuccessNotification = false;

  verificationForm: UntypedFormGroup;

  enable2faEmailVerification = false;

  @ViewChild(TabsComponent)
  tabsComponent: TabsComponent;

  private subscriptions = new Subscription();

  constructor(
    public twoFactorAuthService: TwoFactorAuthService,
    private fb: UntypedFormBuilder,
    private gtmService: GtmService,
    private profileService: ProfileService,
    private aviosLinkingService: AviosLinkingService,
    private configService: ConfigService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.enable2faEmailVerification = this.configService.cfg.enable2faEmailVerification;

    this.profile$ = this.profileService.getProfile().pipe(finShare());

    this.aviosLinkingStatus$ = this.aviosLinkingService.status$;

    this.verificationForm = this.fb.group({
      verificationCode: this.fb.control(
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(6),
          Validators.pattern(/^\d+$/),
        ])
      ),
    });
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  resetAuthState(focusTab = true): void {
    this.verificationForm.reset();
    this.authState = AuthState.CHOOSE_TYPE;
    this.secretKey = undefined;
    this.isRequestLoading = false;
    this.authError = undefined;
    this.resendEmailCount = 0;

    if (focusTab) {
      const activeTab = this.tabsComponent.tabsNavi.nativeElement.querySelector('.tab--active')
        .firstChild as HTMLButtonElement;
      activeTab.focus();
    }
  }

  onCloseError(): void {
    this.authError = undefined;
  }

  selectTwoFactorAuthType(authType: TwoFactorAuthType): void {
    if (authType === TwoFactorAuthType.EMAIL) {
      this.send2faEmailVerificationCode();
    } else if (authType === TwoFactorAuthType.SMS) {
      this.subscriptions.add(
        this.twoFactorAuthService.enableSMSAuth().subscribe({
          next: () => {
            this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            this.trackGTM(TrackGtmActivatingStatus.ACTIVATING_SUCCESSFUL);
          },
          error: () => {
            this.authError = AuthError.Enable2faSms;
            this.trackGTM(TrackGtmActivatingStatus.ACTIVATING_FAILED);
          },
        })
      );
    } else if (authType === TwoFactorAuthType.APP) {
      this.isRequestLoading = true;

      this.subscriptions.add(
        this.twoFactorAuthService.enableGAuthValidated().subscribe({
          next: (response) => {
            this.isRequestLoading = false;
            this.authState = AuthState.VERIFY;
            this.qrCodeImageUrl$ = this.twoFactorAuthService.getQRCodeImageUrl(response.secretKey);
            this.secretKey = response.secretKey;
          },
          error: () => {
            this.isRequestLoading = false;
            this.authState = AuthState.CHOOSE_TYPE;
            this.authError = AuthError.Enable2faApp;
          },
        })
      );
    }
  }

  send2faEmailVerificationCode(isResend = false): void {
    if (isResend) {
      this.resendEmailCount++;

      if (this.resendEmailCount > 2) {
        return;
      }
    }

    this.isRequestLoading = true;
    this.showSend2faEmailVerificationCodeSuccessNotification = false;
    this.authError = undefined;

    this.subscriptions.add(
      this.twoFactorAuthService.send2faEmailVerificationCode().subscribe({
        next: () => {
          this.isRequestLoading = false;

          if (isResend) {
            this.showSend2faEmailVerificationCodeSuccessNotification = true;
          } else {
            this.authState = AuthState.VERIFY;
          }
        },
        error: () => {
          this.isRequestLoading = false;
          this.authError = isResend ? AuthError.Resend2faEmail : AuthError.Enable2faEmail;
        },
      })
    );
  }

  validateCode(twoFactorAuthType: TwoFactorAuthType): void {
    if (this.verificationForm.invalid) {
      this.verificationForm.markAllAsTouched();
      return;
    }

    this.showSend2faEmailVerificationCodeSuccessNotification = false;
    this.authError = undefined;

    const verificationCode = this.verificationForm.get('verificationCode').value;

    if (twoFactorAuthType === TwoFactorAuthType.EMAIL) {
      this.subscriptions.add(
        this.twoFactorAuthService.validate2faEmailVerificationCode(verificationCode).subscribe({
          next: () => {
            this.authState = AuthState.CHOOSE_TYPE;
            this.verificationForm.reset();
            this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            this.trackGTM(TrackGtmActivatingStatus.ACTIVATING_SUCCESSFUL);
          },
          error: (error: unknown) => {
            const errorResponseData = mapError(error);

            if (
              isValidate2faEmailVerificationCodeErrorResponse(errorResponseData) &&
              errorResponseData.reason.toLowerCase().includes('token not found or expired')
            ) {
              this.authError = AuthError.IncorrectCode;
            } else {
              this.authError = AuthError.ValidateCode;
            }

            this.trackGTM(TrackGtmActivatingStatus.ACTIVATING_FAILED);
          },
        })
      );
    } else {
      this.subscriptions.add(
        this.twoFactorAuthService.validateGAuth(verificationCode).subscribe({
          next: (response) => {
            if (response.valid) {
              this.authState = AuthState.CHOOSE_TYPE;
              this.verificationForm.reset();
              this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              this.trackGTM(TrackGtmActivatingStatus.ACTIVATING_SUCCESSFUL);
            } else {
              this.authError = AuthError.IncorrectCode;
              this.trackGTM(TrackGtmActivatingStatus.ACTIVATING_FAILED);
            }
          },
          error: () => {
            this.authError = AuthError.ValidateCode;
            this.trackGTM(TrackGtmActivatingStatus.ACTIVATING_FAILED);
          },
        })
      );
    }
  }

  disableGAuth(): void {
    this.subscriptions.add(
      this.twoFactorAuthService.disableGAuth().subscribe({
        next: () => {
          this.authState = AuthState.CHOOSE_TYPE;
          this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          this.trackGTM('disabling-successful');
        },
        error: () => {
          this.authError = AuthError.Disable2fa;
          this.trackGTM('disabling-failed');
        },
      })
    );
  }

  openEnable2faSmsConfirmationModal(event: Event): void {
    stopPropagation(event);
    this.isEnable2faSmsConfirmationModalOpen = true;
    this.parsedMobilePhone$ = this.profileService.getProfile().pipe(
      filter(
        ({ parsedMobilePhone: { countryCode, nationalNumber } }) =>
          countryCode !== undefined && nationalNumber !== undefined
      ),
      map(({ parsedMobilePhone: { countryCode, nationalNumber } }) => `+${countryCode} ${nationalNumber}`),
      finShare()
    );
  }

  onBackButtonLinkClick(): void {
    this.gtmService.trackElement(
      'update-profile-2fa-back-button-url',
      GaContext.SERVICE_FORMS,
      ElementTypes.NOTIFICATION,
      undefined,
      ElementActions.CLICK
    );
  }

  onTwoFactorAuthErrorStatusNotificationTryAgainLinkClick(): void {
    this.subscriptions.add(this.twoFactorAuthService.getStatus().subscribe());
  }

  private trackGTM(state: string): void {
    this.gtmService.trackElement(
      `update-profile-2fa-${state}`,
      GaContext.SERVICE_FORMS,
      ElementTypes.MODAL,
      state,
      ElementActions.SUBMIT
    );
  }
}
